import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import { getCurrentUser } from "../services/authApi";
import { getDashboardOverview } from "../services/dashboardApi";

const API_BASE = "https://api.simplif.com";

const SettingsLayer = () => {
  const [activeTab, setActiveTab] = useState(() => {
    const tab = new URLSearchParams(window.location.search).get("tab");
    return tab === "billing" ? "billing" : "general";
  });

  const [billingData, setBillingData] = useState(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingError, setBillingError] = useState("");
  const [billingNotice, setBillingNotice] = useState("");
  const [billingAction, setBillingAction] = useState("");

  const [account, setAccount] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [accountLoading, setAccountLoading] = useState(true);
  const [accountError, setAccountError] = useState("");

  useEffect(() => {
    let active = true;
    Promise.allSettled([getCurrentUser(), getDashboardOverview()]).then((results) => {
      if (!active) return;
      if (results[0].status === "fulfilled") {
        setAccount(results[0].value?.user || null);
      }
      if (results[1].status === "fulfilled") {
        setTenant(results[1].value?.tenant || null);
      }
      if (results.some((result) => result.status === "rejected")) {
        setAccountError("Some account details could not be loaded. Refresh to retry.");
      }
      setAccountLoading(false);
    });
    return () => { active = false; };
  }, []);

  const email = account?.email || (accountLoading ? "Loading..." : "Unavailable");
  const brandName = tenant?.name || (accountLoading ? "Loading..." : "Unavailable");
  const brandId = tenant?.brandId ?? tenant?.brand_id ?? tenant?.id ?? (accountLoading ? "Loading..." : "Unavailable");

  const loadBilling = async () => {
    setBillingLoading(true);
    setBillingError("");

    try {
      const response = await fetch(
        `${API_BASE}/v1/dashboard/billing`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            Accept: "application/json",
          },
        }
      );

      const body = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          body.error || "Unable to load billing information."
        );
      }

      setBillingData(body);
    } catch (error) {
      setBillingError(
        error?.message || "Unable to load billing information."
      );
    } finally {
      setBillingLoading(false);
    }
  };

  useEffect(() => {
    const resetBillingAction = () => setBillingAction("");

    window.addEventListener("pageshow", resetBillingAction);

    return () => {
      window.removeEventListener("pageshow", resetBillingAction);
    };
  }, []);

  useEffect(() => {
    if (
      activeTab === "billing" &&
      !billingData &&
      !billingLoading
    ) {
      loadBilling();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const openCheckout = async (planCode) => {
    setBillingAction(planCode);
    setBillingError("");

    try {
      const response = await fetch(
        `${API_BASE}/v1/dashboard/billing/checkout`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            plan: planCode,
          }),
        }
      );

      const body = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          body.error || "Unable to start checkout."
        );
      }

      if (!body.checkout?.url) {
        throw new Error(
          "Stripe Checkout URL was not returned."
        );
      }

      window.location.href = body.checkout.url;
    } catch (error) {
      setBillingError(
        error?.message || "Unable to start checkout."
      );
      setBillingAction("");
    }
  };

  const openPlanChange = async (planCode) => {
    const action = `plan:${planCode}`;

    setBillingAction(action);
    setBillingError("");
    setBillingNotice("");

    try {
      const response = await fetch(
        `${API_BASE}/v1/dashboard/billing/change-plan`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            plan: planCode,
          }),
        }
      );

      const body = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          body.error || "Unable to change billing plan."
        );
      }

      if (body.planChange?.alreadyScheduled) {
        setBillingNotice(
          "Your downgrade to Basic is already scheduled for the end of the current billing period."
        );
        setBillingAction("");
        await loadBilling();
        return;
      }

      if (!body.portal?.url) {
        throw new Error(
          "Stripe plan change URL was not returned."
        );
      }

      window.location.href = body.portal.url;
    } catch (error) {
      setBillingError(
        error?.message || "Unable to change billing plan."
      );
      setBillingAction("");
    }
  };
  const openPortal = async () => {
    setBillingAction("portal");
    setBillingError("");

    try {
      const response = await fetch(
        `${API_BASE}/v1/dashboard/billing/portal`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({}),
        }
      );

      const body = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          body.error || "Unable to open billing portal."
        );
      }

      if (!body.portal?.url) {
        throw new Error(
          "Stripe Billing Portal URL was not returned."
        );
      }

      window.location.href = body.portal.url;
    } catch (error) {
      setBillingError(
        error?.message || "Unable to open billing portal."
      );
      setBillingAction("");
    }
  };

  const selectTab = (tab) => {
    setActiveTab(tab);

    const url = new URL(window.location.href);

    if (tab === "billing") {
      url.searchParams.set("tab", "billing");
    } else {
      url.searchParams.delete("tab");
    }

    window.history.replaceState(
      {},
      "",
      `${url.pathname}${url.search}${url.hash}`
    );
  };

  const formatMoney = (cents, currency = "usd") => {
    const amount = Number(cents || 0) / 100;

    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: String(currency || "usd").toUpperCase(),
      minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatNumber = (value) =>
    new Intl.NumberFormat("en-US").format(
      Number(value || 0)
    );

  const displayPlanName = (plan) => {
    const code = String(plan?.code || "").toLowerCase();
    const name = String(plan?.name || "");

    if (code === "developer" || name.toLowerCase() === "developer") {
      return "Basic";
    }

    return name;
  };

  const formatDate = (value) => {
    if (!value) {
      return "—";
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return "—";
    }

    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(date);
  };

  const billing = billingData?.billing || null;
  const plans = billingData?.plans || [];
  const subscription = billing?.subscription || null;
  const usage = billing?.usage || null;

  const currentPlanCode = billing?.plan?.code;
  const currentPlanPrice =
    Number(billing?.plan?.monthlyPriceCents || 0);

  const hasExistingSubscription = Boolean(
    subscription?.hasStripeSubscription
  );

  const checkoutResult = new URLSearchParams(
    window.location.search
  ).get("checkout");

  return (
    <div className="row gy-4">
      <div className="col-12">
        <div className="card">
          <div className="card-body p-24">
            <div className="d-flex align-items-center gap-16 flex-wrap">
              <button
                type="button"
                className={`btn ${
                  activeTab === "general"
                    ? "btn-primary"
                    : "btn-outline-primary"
                }`}
                onClick={() => selectTab("general")}
              >
                General
              </button>

              <button
                type="button"
                className={`btn ${
                  activeTab === "billing"
                    ? "btn-primary"
                    : "btn-outline-primary"
                }`}
                onClick={() => selectTab("billing")}
              >
                Billing
              </button>
            </div>
          </div>
        </div>
      </div>

      {activeTab === "general" && (
        <div className="col-12">
          <div className="card">
            <div className="card-header bg-base border-bottom px-24 py-20">
              <h5 className="mb-4">
                Account Settings
              </h5>

              <p className="text-secondary-light mb-0">
                Manage your Simplif account and workspace
                settings.
              </p>
            </div>

            <div className="card-body p-24">
              {accountError && <div className="alert alert-warning mb-16" role="alert">{accountError}</div>}
              <div className="row gy-4">
                <div className="col-lg-6">
                  <label className="form-label fw-semibold">
                    Email
                  </label>

                  <input
                    type="email"
                    className="form-control"
                    value={email}
                    readOnly
                  />
                </div>

                <div className="col-lg-6">
                  <label className="form-label fw-semibold">
                    Brand Name
                  </label>

                  <input
                    type="text"
                    className="form-control"
                    value={brandName}
                    readOnly
                  />
                </div>

                <div className="col-lg-6">
                  <label className="form-label fw-semibold">
                    Brand ID
                  </label>

                  <input
                    type="text"
                    className="form-control"
                    value={brandId}
                    readOnly
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === "billing" && (
        <>
          <div className="col-12">
            <div className="card">
              <div className="card-header bg-base border-bottom px-24 py-20">
                <h5 className="mb-4">
                  Billing & Subscription
                </h5>

                <p className="text-secondary-light mb-0">
                  Choose the plan that matches the number of
                  signups Simplif evaluates each month.
                </p>
              </div>

              <div className="card-body p-24">
                {checkoutResult === "success" && (
                  <div
                    className="alert alert-success mb-24"
                    role="alert"
                  >
                    Payment completed successfully. Your
                    subscription is being updated.
                  </div>
                )}

                {checkoutResult === "cancelled" && (
                  <div
                    className="alert alert-warning mb-24"
                    role="alert"
                  >
                    Checkout was cancelled. No changes were
                    made.
                  </div>
                )}

                {billingError && (
                  <div
                    className="alert alert-danger mb-24"
                    role="alert"
                  >
                    {billingError}
                  </div>
                )}

                {billingNotice && (
                  <div
                    className="alert alert-info mb-24"
                    role="status"
                  >
                    {billingNotice}
                  </div>
                )}

                {billingLoading && !billingData ? (
                  <div className="py-32 text-center text-secondary-light">
                    Loading billing information...
                  </div>
                ) : !billing ? (
                  <div className="py-32 text-center">
                    <Icon
                      icon="solar:danger-triangle-outline"
                      className="text-warning-main text-xxl mb-12"
                    />

                    <h6 className="mb-8">
                      Billing information unavailable
                    </h6>

                    <p className="text-secondary-light mb-16">
                      We could not load your billing information.
                    </p>

                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={loadBilling}
                    >
                      Try Again
                    </button>
                  </div>
                ) : (
                  <div className="row gy-4">
                    {plans.map((plan) => {
                      const current =
                        plan.code === currentPlanCode;

                      const canCheckout =
                        plan.selfService &&
                        !current &&
                        !hasExistingSubscription;

                      const isDowngrade =
                        Number(plan.monthlyPriceCents || 0) <
                        currentPlanPrice;

                      return (
                        <div
                          className="col-xl-4 col-md-6"
                          key={plan.code}
                        >
                          <div
                            className={`border radius-12 p-24 h-100 position-relative ${
                              current
                                ? "border-primary-600"
                                : ""
                            }`}
                          >
                            {current && (
                              <span className="badge bg-primary-600 position-absolute top-0 end-0 m-16">
                                Current Plan
                              </span>
                            )}

                            <div className="d-flex flex-column h-100">
                              <div className="mb-20">
                                <h5 className="mb-8">
                                  {displayPlanName(plan)}
                                </h5>

                                <div className="d-flex align-items-end gap-4">
                                  <h3 className="mb-0">
                                    {formatMoney(
                                      plan.monthlyPriceCents,
                                      plan.currency
                                    )}
                                  </h3>

                                  <span className="text-secondary-light mb-4">
                                    / month
                                  </span>
                                </div>
                              </div>

                              <div className="d-flex align-items-center gap-8 mb-24">
                                <Icon
                                  icon="solar:check-circle-bold"
                                  className="text-success-main text-xl"
                                />

                                <span className="text-sm">
                                  {formatNumber(
                                    plan.monthlyEvaluationLimit
                                  )}{" "}
                                  signups evaluated / month
                                </span>
                              </div>

                              <div className="mt-auto">
                                {current ? (
                                  <button
                                    type="button"
                                    className="btn btn-outline-primary w-100"
                                    disabled
                                  >
                                    Current Plan
                                  </button>
                                ) : hasExistingSubscription ? (
                                  <button
                                    type="button"
                                    className="btn btn-outline-primary w-100"
                                    disabled={Boolean(
                                      billingAction
                                    )}
                                    onClick={() =>
                                      openPlanChange(plan.code)
                                    }
                                  >
                                    {billingAction ===
                                    `plan:${plan.code}`
                                      ? isDowngrade
                                        ? "Opening Downgrade..."
                                        : "Opening Upgrade..."
                                      : isDowngrade
                                      ? "Downgrade"
                                      : "Upgrade"}
                                  </button>
                                ) : canCheckout ? (
                                  <button
                                    type="button"
                                    className="btn btn-outline-primary w-100"
                                    disabled={Boolean(
                                      billingAction
                                    )}
                                    onClick={() =>
                                      openCheckout(plan.code)
                                    }
                                  >
                                    {billingAction === plan.code
                                      ? "Opening Checkout..."
                                      : "Upgrade"}
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    className="btn btn-outline-secondary w-100"
                                    disabled
                                  >
                                    Unavailable
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          {billing && (
            <div className="col-12">
              <div className="card">
                <div className="card-header bg-base border-bottom px-24 py-20">
                  <h5 className="mb-4">
                    Current Subscription
                  </h5>

                  <p className="text-secondary-light mb-0">
                    Your current plan, usage and billing period.
                  </p>
                </div>

                <div className="card-body p-24">
                  <div className="row gy-4">
                    <div className="col-xl-3 col-md-6">
                      <div className="border radius-12 p-20 h-100">
                        <span className="text-secondary-light text-sm d-block mb-8">
                          Current Plan
                        </span>

                        <h5 className="mb-4">
                          {billing.plan.name}
                        </h5>

                        <span className="badge bg-success-focus text-success-main">
                          {subscription?.status || "free"}
                        </span>
                      </div>
                    </div>

                    <div className="col-xl-3 col-md-6">
                      <div className="border radius-12 p-20 h-100">
                        <span className="text-secondary-light text-sm d-block mb-8">
                          Monthly Price
                        </span>

                        <h5 className="mb-0">
                          {formatMoney(
                            billing.plan.monthlyPriceCents,
                            billing.plan.currency
                          )}
                          <span className="text-secondary-light text-sm fw-normal">
                            {" "}
                            / month
                          </span>
                        </h5>
                      </div>
                    </div>

                    <div className="col-xl-3 col-md-6">
                      <div className="border radius-12 p-20 h-100">
                        <span className="text-secondary-light text-sm d-block mb-8">
                          Signups Evaluated
                        </span>

                        <h5 className="mb-4">
                          {formatNumber(usage?.used)} /{" "}
                          {formatNumber(usage?.limit)}
                        </h5>

                        <span className="text-secondary-light text-sm">
                          {formatNumber(usage?.remaining)} remaining
                        </span>
                      </div>
                    </div>

                    <div className="col-xl-3 col-md-6">
                      <div className="border radius-12 p-20 h-100">
                        <span className="text-secondary-light text-sm d-block mb-8">
                          Current Billing Period
                        </span>

                        <h6 className="mb-0">
                          {formatDate(
                            billing.period?.start
                          )}{" "}
                          –{" "}
                          {formatDate(
                            billing.period?.end
                          )}
                        </h6>
                      </div>
                    </div>
                  </div>

                  <div className="mt-24">
                    <div className="d-flex align-items-center justify-content-between gap-12 mb-8">
                      <span className="fw-semibold">
                        Monthly signup evaluation usage
                      </span>

                      <span className="text-secondary-light text-sm">
                        {Number(
                          usage?.percentage || 0
                        ).toFixed(1)}
                        %
                      </span>
                    </div>

                    <div
                      className="progress"
                      style={{
                        height: "8px",
                      }}
                    >
                      <div
                        className="progress-bar"
                        role="progressbar"
                        style={{
                          width: `${Math.min(
                            100,
                            Math.max(
                              0,
                              Number(
                                usage?.percentage || 0
                              )
                            )
                          )}%`,
                        }}
                        aria-valuenow={Math.min(
                          100,
                          Math.max(
                            0,
                            Number(
                              usage?.percentage || 0
                            )
                          )
                        )}
                        aria-valuemin="0"
                        aria-valuemax="100"
                      />
                    </div>
                  </div>

                  {subscription?.cancelAtPeriodEnd && (
                    <div
                      className="alert alert-warning mt-24 mb-0"
                      role="alert"
                    >
                      Your subscription is scheduled to cancel
                      at the end of the current billing period.
                    </div>
                  )}

                  {billing.canManage &&
                    subscription?.hasStripeCustomer && (
                      <div className="mt-24">
                        <button
                          type="button"
                          className="btn btn-primary"
                          disabled={Boolean(
                            billingAction
                          )}
                          onClick={openPortal}
                        >
                          {billingAction === "portal"
                            ? "Opening Billing..."
                            : "Manage Billing"}
                        </button>
                      </div>
                    )}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SettingsLayer;
